import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import FormHandler from "../../Common/Form/FormHandler/FormHandler";
import { toast, ToastContainer } from "react-toastify";
import {
  getTaskListURL,
  resourceListURL,
  createMasterSOPURL,
  updateMasterSOPURL,
  getTaskConfigsURL,
  getTaskListURLById,
} from "../../../../Services/EndPoints";
import {
  getData,
  postDataWithBody,
  putDataWithBody,
} from "../../../../Services/Services";
import {
  sopformschema,
  SoPFormConfig,
} from "../../../../JsonData/CreateTaskConfig";
import { Button } from "react-bootstrap";
import "./SOPForm.css";
import { useTranslation } from "react-i18next";
import { LayoutState } from "../../../../Context/LayoutContext/LayoutContext";

const SOPForm = () => {
  // State initialization
  const [dataLoadComplete, setDataLoadComplete] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [SoPConfig, setSoPConfig] = useState(SoPFormConfig);
  const [allTasks, setAllTasks] = useState([]);
  const [configData, setConfigData] = useState(null);
  
  // Use refs to track initialization state and prevent unnecessary re-renders
  const initializationComplete = useRef(false);
  const initialFormSetComplete = useRef(false);
  
  const { state } = useLocation();
  const edit_data = state?.data;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const { collapsed } = LayoutState();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    control,
    formState: { errors },
  } = useForm({ 
    resolver: yupResolver(sopformschema[activeTab]), 
    mode: "all" 
  });

  const selectedIncidentId = watch("incidentId");
  const selectedTaskType = watch("task_type");

  // Load initial data - Get all configurations first
  useEffect(() => {
    const fetchConfigurations = async () => {
      try {
        console.log("Fetching configurations");
        const configRes = await getTaskConfigs();
        setConfigData(configRes);
        initializationComplete.current = true;
      } catch (error) {
        console.error("Error fetching configurations:", error);
        toast.error("Failed to load configuration data", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
      }
    };
    
    fetchConfigurations();
  }, []);

  // Handle edit mode setup AFTER configurations are loaded
  useEffect(() => {
    // Only proceed if config data is loaded and form hasn't been set up yet
    if (!configData || initialFormSetComplete.current || !initializationComplete.current) return;
    
    const setupEditData = async () => {
      try {
        console.log("Setting up edit data");
        
        if (edit_data) {
          console.log("Processing edit data:", edit_data);
          
          // Find the corresponding incident ID
          let incidentIdToSet = "";
          if (edit_data.incident_type) {
            const incidentConfig = configData.find(
              config => 
                config.category_name === "INCDNT_CAT" && 
                config.value === edit_data.incident_type
            );
            
            incidentIdToSet = incidentConfig ? incidentConfig.id.toString() : "";
            console.log("Found incident ID:", incidentIdToSet, "for incident type:", edit_data.incident_type);
          }
          
          // Set form values
          const formData = {
            id: edit_data.id,
            name: edit_data.name,
            description: edit_data.description,
            incidentId: incidentIdToSet,
            task_type: edit_data.task_type_id === 341 ? "0" : "1",
          };
          
          console.log("Setting form data:", formData);
          reset(formData);
          
          // Fetch tasks for the selected incident
          if (incidentIdToSet) {
            console.log("Fetching tasks for initial incident:", incidentIdToSet);
            const taskList = await fetchTaskListData(incidentIdToSet);
            setAllTasks(taskList);
            
            // Apply task type filter
            if (formData.task_type) {
              filterAndUpdateTasksByType(formData.task_type, taskList);
            }
            
            // Set selected tasks if available with a slight delay to ensure options are updated
            if (edit_data.task_id_list && edit_data.task_list) {
              setTimeout(() => {
                populateAttachedTasks(edit_data);
              }, 100);
            }
          }
        }
        
        initialFormSetComplete.current = true;
        setDataLoadComplete(true);
      } catch (error) {
        console.error("Error setting up edit data:", error);
      }
    };
    
    setupEditData();
  }, [configData, edit_data]);

  // Handle incident ID changes
  useEffect(() => {
    // Skip if initialization is not complete
    if (!initializationComplete.current) return;
    
    // Skip if no incident ID selected or in the middle of initial form setup
    if (!selectedIncidentId || !initialFormSetComplete.current) return;
    
    const handleIncidentChange = async () => {
      try {
        console.log("Incident changed to:", selectedIncidentId);
        
        // Clear current tasks selection
        setValue("sop_task", []);
        
        // Fetch tasks for the selected incident
        const taskList = await fetchTaskListData(selectedIncidentId);
        setAllTasks(taskList);
        
        // Apply task type filter if available
        if (selectedTaskType) {
          filterAndUpdateTasksByType(selectedTaskType, taskList);
        }
      } catch (error) {
        console.error("Error handling incident change:", error);
      }
    };
    
    handleIncidentChange();
  }, [selectedIncidentId]);

  // Handle task type changes
  useEffect(() => {
    // Skip if initialization is not complete or in the middle of initial form setup
    if (!initializationComplete.current || !initialFormSetComplete.current) return;
    
    // Skip if no tasks loaded
    if (allTasks.length === 0) return;
    
    if (selectedTaskType) {
      console.log("Task type changed to:", selectedTaskType);
      setValue("sop_task", []);
      filterAndUpdateTasksByType(selectedTaskType, allTasks);
    }
  }, [selectedTaskType, allTasks.length]);

  // Fetch task configuration data
  async function getTaskConfigs() {
    try {
      const res = await postDataWithBody(
        getTaskConfigsURL,
        ["TSK_PRIOR", "INCDNT_CAT", "TSK_STAT", "TSK_TYP"],
        {}
      );
      
      // Process configuration data
      const config_type = {};
      res.forEach(config => {
        if (!config_type[config.category_name]) {
          config_type[config.category_name] = [];
        }
        
        config_type[config.category_name].push({
          label: config.value,
          value: config.id,
        });
      });
      
      // Update SoPConfig with incident categories
      const updatedConfig = [...SoPConfig];
      const newFields = [...updatedConfig[0].fields];
      
      // Update incident field options
      newFields[0] = {
        ...newFields[0],
        options: config_type["INCDNT_CAT"] || []
      };
      
      updatedConfig[0] = {
        ...updatedConfig[0],
        fields: newFields
      };
      
      setSoPConfig(updatedConfig);
      console.log("Updated config with incident categories");
      
      return res;
    } catch (error) {
      console.error("Error fetching task configs:", error);
      toast.error("Failed to fetch configuration data", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      throw error;
    }
  }

  function filterAndUpdateTasksByType(taskType, tasks = allTasks) {
    try {
      // Convert taskType value to label
      const taskTypeLabel = taskType === "0" ? "Activity" : "Notification";
  
      // Filter tasks based on task_type
      const filteredTasks = tasks.filter(
        task => task.task_type === taskTypeLabel
      );
  
      // Format tasks for dropdown
      const taskOptions = filteredTasks.map(task => ({
        label: task.task_name,
        value: task.id.toString(),
      }));
  
      // Update config with filtered tasks - ONLY update task field
      setSoPConfig(prevConfig => {
        // Create deep copies to avoid reference issues
        const updatedConfig = JSON.parse(JSON.stringify(prevConfig));
        
        // Only update the task field (index 2) and preserve all other fields
        if (updatedConfig[0] && updatedConfig[0].fields && updatedConfig[0].fields[2]) {
          updatedConfig[0].fields[2].options = taskOptions;
        }
        
        return updatedConfig;
      });
      
      console.log("Updated task options for task type:", taskType, "with", taskOptions.length, "options");
    } catch (error) {
      console.error("Error filtering tasks:", error);
    }
  }

  // Fetch task list data based on incident ID
  async function fetchTaskListData(incident_id) {
    try {
      if (!incident_id) return [];
      
      // Get tasks for the selected incident
      const taskListUrl = getTaskListURLById.replace("ID", incident_id);
      console.log("Fetching tasks from:", taskListUrl);
      const result = await getData(taskListUrl);
      console.log("Received tasks:", result.length);
      return result;
    } catch (error) {
      console.error("Error fetching task list:", error);
      toast.error("Failed to fetch tasks", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      return [];
    }
  }

  // Populate tasks from edit data
  function populateAttachedTasks(data) {
    try {
      if (!data.task_id_list || !data.task_list) {
        console.log("No tasks to populate");
        return;
      }
      
      // Parse task IDs and names
      const taskIds = data.task_id_list.split(",");
      const taskNames = data.task_list.split(",");
      
      // Create selected tasks array
      const selectedTasks = taskIds.map((id, index) => ({
        value: id.trim(),
        label: (taskNames[index] || id).trim()
      }));
      
      // Set form value if tasks exist
      if (selectedTasks.length > 0) {
        console.log("Setting sop_task value with", selectedTasks.length, "tasks:", selectedTasks);
        setValue("sop_task", selectedTasks);
      }
    } catch (error) {
      console.error("Error populating tasks:", error);
    }
  }

  // Generate form fields
  const GenerateForm = (fields, register, errors, control) => {
    return (
      <div>
        <FormHandler
          register={register}
          errors={errors}
          fields={fields}
          control={control}
          columns={2}
          getValues={getValues}
        />
      </div>
    );
  };

  // Generate tab data
  const generateTabsData = (tabWiseData) => {
    return tabWiseData.map(element => ({
      ...element,
      component: GenerateForm(
        element.fields,
        register,
        errors,
        control
      )
    }));
  };

  const tabsData = generateTabsData(SoPConfig);

  // Navigation handlers
  const navToCreateTask = () => {
    navigate("../create-task");
  };
  
  const navToCreateResource = () => {
    navigate("../../resource-list/create-resource");
  };
  
  // Form submission handler
  const submitHandler = async (data, e) => {
    e.preventDefault();
    setIsDisabled(true);
    
    try {
      data.status = true;
      
      // Process tasks
      const sop_task_map = data.sop_task?.map(task => ({
        task_id: task.value,
        ...(edit_data ? { sop_id: data.id } : {})
      })) || [];
      
      data.sop_task_map = sop_task_map;
      
      // Log the final form data being submitted
      console.log("Submitting form data:", data);
      
      // Submit data
      let res;
      if (edit_data) {
        res = await putDataWithBody(updateMasterSOPURL, data, {});
      } else {
        res = await postDataWithBody(createMasterSOPURL, data, {});
      }

      // Handle response
      if (res.status === "success") {
        toast.success(res.msg, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        navigate("../view-sop");
      } else {
        toast.error(res.msg, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("An error occurred while saving the SOP", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsDisabled(false);
    }
  };

  return (
    <div className="mt-4 facility-container">
      <div className="form-body">
        <div className="registration__form--body">
          <div
            onClick={navToCreateTask}
            className={`add_task_sop_mgmnt ${collapsed ? "" : "expanded"}`}
          >
            {t("addtask")}
          </div>
          {/* <div
            onClick={navToCreateResource}
            className={`add_resource_sop_mgmnt ${collapsed ? "" : "expanded"}`}
          >
            {t("addresource")}
          </div> */}
          <form onSubmit={handleSubmit(submitHandler)}>
            <div>
              {tabsData?.map((tab) => (
                <React.Fragment key={tab.id}>
                  {tab.id === activeTab && (
                    <div>{tab.component}</div>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="d-flex justify-content-center">
              {tabsData.length - 1 === activeTab && (
                <Button
                  type="submit"
                  className="mx-2"
                  variant="primary"
                  disabled={isDisabled}
                >
                  {edit_data ? t("Update") : t("saveAndSubmit")}
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default SOPForm;